import { OptionHelpers } from '../../helpers/option-helpers';
import { StringHelpers } from '../../helpers/string-helpers';
import { IChartContainer } from '../../models/base/chart-container.interface';
import { ChartStyleSettingsModel } from '../../models/chart-style/chart-style-settings.model';
import { TimeframeOptionModel } from '../../models/drop-down-list/timeframe-option.model';
import { Timeframe } from '../../models/enum/timeframe.enum';
import { TiChartMenuContainer } from "../../ti-chart-menu.container";
import { IComponent } from "../component.interface";
import { DrawTradePriceAnnotationComponent } from '../draw-trade-price-annotation.model';
import { PnlComponent } from '../pnl.component';

export class TimeframeIconComponent implements IComponent {
    public id = StringHelpers.newGuid();

    private timeframeOptions = OptionHelpers.getTimeframeOptions();
    private currentTimeframe: TimeframeOptionModel = this.timeframeOptions[0];
    private tiChartMenuContainer: TiChartMenuContainer;
    private base: IChartContainer;
    private element: HTMLElement;
    private timeSpanElement: HTMLElement;
    private clockStylesOption: HTMLElement;
    private options: NodeListOf<HTMLElement>;

    private chartStyle: ChartStyleSettingsModel | null = null;

    private moveClockStyleToWrapper = false;

    constructor(el: HTMLElement, tiChartMenuContainer: TiChartMenuContainer, base: IChartContainer) {
        this.element = el;
        this.timeSpanElement = this.element.findFirst('.timespan');
        this.tiChartMenuContainer = tiChartMenuContainer;
        this.base = base;

        this.moveClockStyleToWrapper = this.base.marketingWebChartMode && !this.base.pnpMode && !this.base.isPreview;

        this.setWebChartSize();
        this.onClick();
        this.subscribeChartData();
        this.onOptionsClick();
        this.onResize();
    }

    public show() {
        this.element.addClass('active');
    }

    public hide() {
        this.element.removeClass('active');
        this.hideWebChartOptions();
    }

    public setCurrentTimeframe(timeframe: Timeframe) {
        let op: HTMLElement = null;
        
        this.options.forEach((el: HTMLElement) => {
            if (el.dataset.value == timeframe + '') {
                op = el;
            }
        });
        
        if (!op) { return; }
        this.setElementActive(op);
        const option = this.timeframeOptions.find((x: TimeframeOptionModel) => x.value == parseInt(op.dataset.value, 10));
        if (option) {
            this.currentTimeframe = option;
            this.setTimeSpanLabel();
        
        }
    }
    
    public showTimespan() {
        this.timeSpanElement?.removeClass('hidden');
    }

    public hideTimespan() {
        this.timeSpanElement?.addClass('hidden');
    }

    public setWebChartSize() {
        if (!this.base.marketingWebChartMode) {
            return;
        }

        this.element.removeClass('small');
        const parent = this.element.parentElement;
        if (parent.scrollWidth > parent.clientWidth) {
            this.element.addClass('small');
        }

        if (!this.base.pnpMode) {
            if (this.chartStyle?.marketingChartTimeframeAutoExpand) {
                this.element.classList.remove('clock-styles');
                if (this.base.element.clientWidth <= 675) {
                    this.element.classList.add('clock-styles');
                }
            } else {
                this.element.classList.add('clock-styles');
            }
        }
    }

    private subscribeChartData() {
        this.base.chartService.subscribeChartStyleData((style: ChartStyleSettingsModel) => {
            this.chartStyle = style;

            if (!this.base.marketingWebChartMode && this.base.pnpMode) {
                if (style.pnpTimeframeRangeEnabled) {
                    this.element.findFirst('ul.normal')?.remove();
                } else {
                    this.element.findFirst('ul.range')?.remove();
                    this.element.findFirst('.timespan')?.remove();
                }
            }
            
            this.setWebChartSize();
        });
    }

    private showMenu() {
        const show = !this.tiChartMenuContainer.displayTimeframe;
        if (this.moveClockStyleToWrapper && this.clockStylesOption) {
            if (!this.added) {
                this.base.chartWrapper.insertAdjacentElement('beforeend', this.clockStylesOption);
            }

            this.setElementPosition();
            if (show) {
                this.clockStylesOption.classList.add('active')
            } else {
                this.clockStylesOption.classList.remove('active')
            }
        }

        this.toggleTimeframe(show);
    }

    private setElementPosition() {
        if (this.clockStylesOption) {
            const parentRect = this.base.element.getBoundingClientRect();
            const rect = this.element.getBoundingClientRect();
            this.clockStylesOption.style.top =  ((rect.y - parentRect.y) + 24) + 'px';
            this.clockStylesOption.style.left = (rect.x - parentRect.x) + 'px';
        }
    }

    private added = false;
    private onClick() {
        this.element.addEventListener('mouseenter', () => {
            if (!this.base.isTouchDevice || !this.base.marketingWebChartMode) {
                return;
            }

            this.showMenu();
        });
        this.element.addEventListener('click', (event: any) => {
            event.stopPropagation();
            this.showMenu();
        });
    }

    private onOptionsClick() {
        this.options = this.element.querySelectorAll<HTMLElement>('.tf');
        this.options.forEach((op: HTMLElement) => {
            op.addEventListener('mouseenter', (ev: any) => {
                if (!this.base.isTouchDevice || !this.base.marketingWebChartMode) {
                    return;
                }
    
                ev.stopPropagation();
                this.selectTimeframeOption(op);
            });
            op.addEventListener('click', (ev: any) => {
                ev.stopPropagation();
                this.selectTimeframeOption(op);
            });
        });
        
        if (this.moveClockStyleToWrapper) {
            // Move clock stlyes options
            this.clockStylesOption = this.element.findFirst('ul.clock');
            this.clockStylesOption.classList.add('clock-list');
            this.element.removeChild(this.clockStylesOption);
        }
    }

    private toggleTimeframe(show: boolean) {
        this.tiChartMenuContainer.displayTimeframe = show;
        this.tiChartMenuContainer.displayTools = false;
        this.tiChartMenuContainer.displayIndicator = false;
        this.tiChartMenuContainer.displayChartType = false;
        this.tiChartMenuContainer.displayTradeOrderType = false;
        this.base.hideTooltip();
        this.tiChartMenuContainer.toggleControls();
    }

    private selectTimeframeOption(op: HTMLElement | null) {
        if (!op) { return; }
        this.setElementActive(op);
        const option = this.timeframeOptions.find((x: TimeframeOptionModel) => x.value == parseInt(op.dataset.value, 10));
        if (this.currentTimeframe && option && this.currentTimeframe.value != option.value) {
            this.changeTimeframe(option);
        }
        this.tiChartMenuContainer.closeAllMenuControls();
    }
    
    private setElementActive(op: HTMLElement) {
        this.removeActiveElements();
        this.options.forEach((x: HTMLElement) => {
            if (x.dataset.value == op.dataset.value) {
                x.classList.add('active');
            }
        });
    }

    private setTimeSpanLabel() {
        if (!this.base.pnpMode) return;

        if (!this.currentTimeframe.timespan) {
            this.timeSpanElement?.addClass('hidden');
        } else {
            this.timeSpanElement?.setInnerHtml(this.currentTimeframe.timespan);
            this.timeSpanElement?.removeClass('hidden');
        }
    }

    private changeTimeframe(timeframe: TimeframeOptionModel) {
        if (!this.base.tiChartSeries || this.currentTimeframe.value === timeframe.value) {
            return;
        }

        this.base.tiChartSeries.ready = false;
        this.currentTimeframe = timeframe;
        this.setTimeSpanLabel();
        
        const defaultTimeframe = timeframe.value >= 21 ? Timeframe.OneMonthRange : Timeframe.Day;
        const pnlComponent = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        pnlComponent?.hideElements();

        const drawTradePriceAnnotationComponent = this.base.getComponentsByType(DrawTradePriceAnnotationComponent)[0] as DrawTradePriceAnnotationComponent;
        drawTradePriceAnnotationComponent?.remove();
        
        this.base.tiChartSeries.highChartComparisonBuilder?.removeComparisonsComponents(true);
        
        if (this.base.marketingWebChartMode && this.base.pnpMode) {
            const marketingContainer = this.base as any;
            const pnpChartService = marketingContainer.pnpContainer?.chartService;
            
            if (pnpChartService && marketingContainer.pnpSwitchComponent && marketingContainer.pnpSwitchComponent.switched) {
                marketingContainer.pnpSwitchComponent.togglePnPMode();
                pnpChartService.requestNewTimeframe(defaultTimeframe);
            }
        } else if (!this.base.marketingWebChartMode) {
            const container = this.base as any;
            const pnpChartService = container.pnpContainer?.chartService;

            if (pnpChartService && container.pnpSwitchComponent && container.pnpSwitchComponent.switched) {
                container.pnpSwitchComponent.togglePnPMode();
                pnpChartService.requestNewTimeframe(defaultTimeframe);
            } else {
                this.base.setComponentStateWhenRequestingNewSymbol(this.base.tiChartSeries.symbol, this.currentTimeframe.value);
            }
        }

        if (this.base.pnpMode) {
            this.base.tiChartSeries.currentTimeframe = timeframe.value;
            this.base.tiChartSeries.setPanel();
        }

        this.base.chartService.requestNewTimeframe(timeframe.value);
    }

    private removeActiveElements() {        
        this.options.forEach((op: HTMLElement) => {
            op.classList.remove('active');
        });
    }
 
    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.toggleTimeframe(false);
                this.setWebChartSize();
            }, 150);
        });
    }

    private hideWebChartOptions() {
        if (this.base.marketingWebChartMode && this.clockStylesOption && !this.base.isPreview) {
            this.element.removeClass('active');
            this.clockStylesOption.removeClass('active');
            this.tiChartMenuContainer.displayTimeframe = false;
        }
    }
}
